package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text

/**
 * A credential the product will never show again.
 *
 * **Why this is not [LCodeBlock].** That component deliberately refuses to render anything matching
 * a credential shape, which is right where a block is quoting configuration and wrong here: this is
 * the one moment the value is legitimately on screen. It is also not a bare `<pre>`, which is what
 * three of these surfaces had. `<pre>` defaults to `white-space: pre`, so a forty-character token
 * does not wrap and has no scroller — inside a card on a phone the tail is pushed off the page, and
 * the only recovery left is selecting a mono run by hand, on a card whose button navigates away.
 *
 * So the value scrolls rather than escaping, and copying is a button rather than a drag. The server
 * keeps only a hash, so a value lost at this moment is lost for good; that is the whole reason the
 * copy affordance belongs to the component instead of to whoever remembers to add it.
 *
 * [onCopyFailed] exists because the clipboard is refusable — permissions, an insecure origin, an
 * unfocused document — and a copy button that silently does nothing is worse than no button at all.
 */
@Composable
fun LOneTimeSecret(
    value: String,
    label: String,
    copyLabel: String,
    modifier: Modifier = Modifier,
    onCopied: () -> Unit = {},
    onCopyFailed: () -> Unit = {},
    trailingAction: (@Composable () -> Unit)? = null,
) {
    val scope = rememberCoroutineScope()
    // **Persistent, unlike every other copy control in the system.** `rememberCopyConfirmation`
    // holds its check for 1.6s and lets go, which is right for a value you can copy again whenever
    // you like. This value you cannot: the server keeps only a hash, so "you have already taken
    // this" is durable state a reader is entitled to see for as long as the panel is open — and two
    // call sites had reached the same conclusion independently, swapping `copyLabel` to "Copied"
    // and holding it there. A glyph that reverted after a second would then be contradicting a
    // label beside it that had not.
    //
    // **Keyed on `value`.** Unkeyed, the flag outlived the secret it was about: rotating a webhook
    // signing secret, copying it, then rotating again swaps `value` without unmounting the panel,
    // and the check stayed lit over a credential nobody had taken. Claiming a one-time value has
    // been saved when it has not is the worst thing this component can do, and the persistence that
    // makes it useful is exactly what made it possible.
    var copied by remember(value) { mutableStateOf(false) }
    Column(modifier.fillMaxWidth().gap(Space.MD)) {
        // `tabindex` so the scroller this establishes can be reached and panned from the keyboard;
        // a region that scrolls but cannot be focused is unreachable without a pointer.
        Pre(
            LCodeBlockStyle.toModifier()
                .then(LCodeBlockPreStyle.toModifier())
                .fillMaxWidth()
                .toAttrs {
                    attr("aria-label", label)
                    attr("data-one-time-secret", "")
                    attr("tabindex", "0")
                },
        ) {
            Code { Text(value) }
        }
        Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
            LButton(
                label = copyLabel,
                onClick = {
                    scope.launch {
                        try {
                            copySafely(value)
                            copied = true
                            onCopied()
                        } catch (cancelled: CancellationException) {
                            throw cancelled
                        } catch (_: Throwable) {
                            onCopyFailed()
                        }
                    }
                },
                // Sized to the button's own icon slot. `LCopyGlyph` wraps its glyphs in a
                // positioned `Span`, so `LButtonIconStyle`'s `> svg` rule — which stretches a
                // *direct* child to fill the slot — never reaches them, and the default `IconSize.MD`
                // drew 16px in an 18px slot, off-centre against every other leading icon in the
                // product. `LButtonSize.MD.iconSize` is 18, which is `IconSize.LG`.
                leadingIcon = { LCopyGlyph(confirmed = copied, size = IconSize.LG) },
            )
            trailingAction?.invoke()
        }
    }
}
